import {
  pixelPolylineLength,
  pixelPolygonArea,
  pixelsToFeet,
  pixelsSqToSF,
  type SheetScale,
} from '@otto/onscreen-engine';
import { COLORS, mono } from './styles.ts';
import type { TakeoffItem } from './helpers.ts';

interface Props {
  items: readonly TakeoffItem[];
  scale: SheetScale | null;
  selectedItemId: string | null;
  hoveredItemId: string | null;
  onSelect: (id: string | null) => void;
  onHover: (id: string | null) => void;
  onDelete: (id: string) => void;
}

export function ItemsCard({
  items,
  scale,
  selectedItemId,
  hoveredItemId,
  onSelect,
  onHover,
  onDelete,
}: Props) {
  if (items.length === 0) {
    return (
      <>
        <div style={{ ...mono(10, COLORS.textDim), letterSpacing: '0.1em', marginBottom: 8 }}>
          ITEMS
        </div>
        <div style={{ fontSize: 12, color: COLORS.textDim }}>None.</div>
      </>
    );
  }

  const linear = items.filter((i) => i.kind === 'linear');
  const polygon = items.filter((i) => i.kind === 'polygon');
  const count = items.filter((i) => i.kind === 'count');

  const linearLF = scale
    ? linear.reduce((sum, i) => sum + pixelsToFeet(pixelPolylineLength(i.points), scale), 0)
    : 0;
  const polygonSF = scale
    ? polygon.reduce((sum, i) => sum + pixelsSqToSF(pixelPolygonArea(i.points), scale), 0)
    : 0;
  const countTotal = count.reduce((sum, i) => sum + i.points.length, 0);

  return (
    <>
      <div style={{ ...mono(10, COLORS.textDim), letterSpacing: '0.1em', marginBottom: 8 }}>
        ITEMS
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {linear.length > 0 && (
          <Group
            label="LINEAR"
            total={`${linearLF.toFixed(1)} LF`}
            items={linear}
            scale={scale}
            selectedItemId={selectedItemId}
            hoveredItemId={hoveredItemId}
            onSelect={onSelect}
            onHover={onHover}
            onDelete={onDelete}
          />
        )}
        {polygon.length > 0 && (
          <Group
            label="POLYGON"
            total={`${polygonSF.toFixed(0)} SF`}
            items={polygon}
            scale={scale}
            selectedItemId={selectedItemId}
            hoveredItemId={hoveredItemId}
            onSelect={onSelect}
            onHover={onHover}
            onDelete={onDelete}
          />
        )}
        {count.length > 0 && (
          <Group
            label="COUNT"
            total={`${countTotal}`}
            items={count}
            scale={scale}
            selectedItemId={selectedItemId}
            hoveredItemId={hoveredItemId}
            onSelect={onSelect}
            onHover={onHover}
            onDelete={onDelete}
          />
        )}
      </div>
    </>
  );
}

function Group({
  label,
  total,
  items,
  scale,
  selectedItemId,
  hoveredItemId,
  onSelect,
  onHover,
  onDelete,
}: {
  label: string;
  total: string;
  items: readonly TakeoffItem[];
  scale: SheetScale | null;
  selectedItemId: string | null;
  hoveredItemId: string | null;
  onSelect: (id: string | null) => void;
  onHover: (id: string | null) => void;
  onDelete: (id: string) => void;
}) {
  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          marginBottom: 4,
        }}
      >
        <span style={{ ...mono(10, COLORS.textDim), letterSpacing: '0.08em' }}>{label}</span>
        <span style={mono(11, COLORS.textMuted)}>{total}</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {items.map((it, i) => (
          <Row
            key={it.id}
            item={it}
            index={i + 1}
            scale={scale}
            selected={selectedItemId === it.id}
            hovered={hoveredItemId === it.id}
            onSelect={onSelect}
            onHover={onHover}
            onDelete={onDelete}
          />
        ))}
      </div>
    </div>
  );
}

function Row({
  item,
  index,
  scale,
  selected,
  hovered,
  onSelect,
  onHover,
  onDelete,
}: {
  item: TakeoffItem;
  index: number;
  scale: SheetScale | null;
  selected: boolean;
  hovered: boolean;
  onSelect: (id: string | null) => void;
  onHover: (id: string | null) => void;
  onDelete: (id: string) => void;
}) {
  const measurement = computeRowMeasurement(item, scale);
  const bg = selected
    ? 'rgba(196,155,255,0.12)'
    : hovered
      ? 'rgba(196,155,255,0.06)'
      : 'transparent';
  const borderColor = selected
    ? 'rgba(196,155,255,0.4)'
    : hovered
      ? 'rgba(196,155,255,0.2)'
      : 'transparent';
  return (
    <div
      onClick={() => onSelect(selected ? null : item.id)}
      onMouseEnter={() => onHover(item.id)}
      onMouseLeave={() => onHover(null)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 6px',
        background: bg,
        border: `1px solid ${borderColor}`,
        borderRadius: 4,
        cursor: 'pointer',
      }}
    >
      <span style={{ ...mono(10, COLORS.textDim), width: 20 }}>{index}</span>
      <span style={{ ...mono(11, COLORS.textMuted), flex: 1 }}>{measurement}</span>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDelete(item.id);
        }}
        title="Delete"
        style={{
          background: 'transparent',
          border: 'none',
          color: COLORS.textDim,
          fontSize: 14,
          cursor: 'pointer',
          padding: '0 4px',
          lineHeight: 1,
        }}
      >
        ×
      </button>
    </div>
  );
}

function computeRowMeasurement(item: TakeoffItem, scale: SheetScale | null): string {
  if (item.kind === 'count') return `${item.points.length} marker${item.points.length === 1 ? '' : 's'}`;
  if (!scale) return '—';
  if (item.kind === 'linear') {
    return `${pixelsToFeet(pixelPolylineLength(item.points), scale).toFixed(1)} LF`;
  }
  return `${pixelsSqToSF(pixelPolygonArea(item.points), scale).toFixed(0)} SF`;
}
